/// <amd-module name="Wit/DeliveryPlans/Board/Components/StickyZone" />
import * as React from "react";
import { IntersectionContext } from "VSSUI/Intersection";
import { shimRef } from "VSSUI/Util";

export interface IStickyZoneProps {
    /**
     * Custom styling of the observed element - useful to let the caller define its position
     */
    observedElementPosition: React.CSSProperties;
    /**
     * Account for headings when calculating updated position of sticky elements
     * @default 0
     */
    threshold?: number;
}

/**
 * StickyZone will watch child elements (must be DOM element, not a custom react component) and update
 * vertical positioning based on scroll position to mimic the CSS sticky positioning effect
 */
export class StickyZone extends React.Component<IStickyZoneProps> {
    public static defaultProps: Partial<IStickyZoneProps> = { threshold: 0 };
    public static contextType = IntersectionContext;

    private stickyRefs: React.RefObject<HTMLElement>[] = [];
    private observedRef = React.createRef<HTMLDivElement>();
    private fixed = false;
    private static observedElementClassName = "observed-element";

    public render() {
        return (
            <IntersectionContext.Consumer>
                {intersectionContext => (
                    <>
                        {React.Children.map(this.props.children, (child: React.DetailedReactHTMLElement<any, HTMLElement>, index) => {
                            // All direct children MUST be DOM elements
                            if (typeof child.type !== "string") {
                                throw Error("Children of a sticky zone MUST be DOM elements");
                            }

                            // shim a ref onto all the children
                            const ref = shimRef<HTMLElement>(child);
                            this.stickyRefs[index] = ref;
                            return React.cloneElement(child, { key: `sticky-${index}`, ...child.props, ref });
                        })}
                        {/** insert a dummy element from which we can measure overlaps */}
                        <div
                            className={StickyZone.observedElementClassName}
                            key="observed-element"
                            style={this.props.observedElementPosition}
                            ref={this.observedRef}
                            role="presentation"
                        />
                    </>
                )}
            </IntersectionContext.Consumer>
        );
    }

    public componentDidMount() {
        this.context.register(this.onScroll);
    }

    public componentWillUnmount() {
        this.context.unregister(this.onScroll);
    }

    private onScroll = (entries: IntersectionObserverEntry[]) => {
        const { threshold } = this.props;
        const spacerTop = this.observedRef.current?.getBoundingClientRect().top;
        if (spacerTop && spacerTop - threshold! < 0) {
            if (!this.fixed) {
                this.fixed = true;
            }
        } else if (this.fixed) {
            this.fixed = false;
            for (const element of this.stickyRefs) {
                element.current?.style.removeProperty("transform");
            }
        }

        if (this.fixed) {
            for (const element of this.stickyRefs) {
                if (element.current && spacerTop) {
                    element.current.style.transform = `translateY(${threshold! - spacerTop}px)`;
                }
            }
        }
    };
}
